import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import CurrentLocationIcon from '../../../../../../assets/images/icons/current-location-icon.svg'

const ElasticsearchHighlight = ({ fallbackText, highlight }) => {
  if (!highlight) return fallbackText || ''

  const parts = highlight.split(/(<em>.*?<\/em>)/g)

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('<em>') && part.endsWith('</em>')) {
          const text = part.slice(4, -5)
          return <span key={index} className='AddressAutocompleteList-highlight'>{text}</span>
        }
        return part
      })}
    </>
  )
}

ElasticsearchHighlight.propTypes = {
  fallbackText: PropTypes.string,
  highlight: PropTypes.string
}

const AddressAutocompleteList = ({
  isLoading = false,
  onSelectCurrentLocation,
  onSelectSuggestion,
  query = '',
  selectedIndex = -1,
  suggestions = []
}) => {
  const hasQuery = query && query.length >= 2
  const hasSuggestions = suggestions.length > 0

  const groupedSuggestions = suggestions.reduce((acc, suggestion) => {
    const groupKey = suggestion.category || 'sales'
    if (!acc[groupKey]) acc[groupKey] = []
    acc[groupKey].push(suggestion)
    return acc
  }, {})

  const salesSuggestions = groupedSuggestions.sales || []
  const rentalsSuggestions = groupedSuggestions.rentals || []

  return (
    <div className='AddressAutocompleteList'>
      {!hasQuery && (
        <div className='AddressAutocompleteList-section'>
          <div
            className='AddressAutocompleteList-currentLocation'
            onClick={onSelectCurrentLocation}
            style={{ cursor: 'pointer' }}
          >
            <div className='AddressAutocompleteList-currentLocationIcon'>
              <img src={CurrentLocationIcon} alt='' />
            </div>
            <div className='AddressAutocompleteList-currentLocationText'>Current Location</div>
          </div>
        </div>
      )}

      {hasQuery && (
        <div className='AddressAutocompleteList-section'>
          {isLoading ? (
            <div className='AddressAutocompleteList-loading'>
              <div className='AddressAutocompleteList-loadingIcon'>⏳</div>
              <div className='AddressAutocompleteList-loadingText'>Searching addresses...</div>
            </div>
          ) : hasSuggestions ? (
            <div className='AddressAutocompleteList-suggestions'>
              {salesSuggestions.length > 0 && (
                <div className='AddressAutocompleteList-group'>
                  <div className='AddressAutocompleteList-sectionTitle'>
                    SALES ({salesSuggestions.length})
                  </div>
                  {salesSuggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.id}
                      className={classNames('AddressAutocompleteList-suggestion', {
                        'AddressAutocompleteList-suggestion--selected': index === selectedIndex
                      })}
                      onClick={() => onSelectSuggestion(suggestion)}
                    >
                      <div className='AddressAutocompleteList-suggestionContent'>
                        <div className='AddressAutocompleteList-suggestionTitle'>
                          <ElasticsearchHighlight
                            highlight={suggestion.highlight}
                            fallbackText={suggestion.title}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {rentalsSuggestions.length > 0 && (
                <div className='AddressAutocompleteList-group'>
                  <div className='AddressAutocompleteList-sectionTitle'>
                    RENTALS ({rentalsSuggestions.length})
                  </div>
                  {rentalsSuggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.id}
                      className={classNames('AddressAutocompleteList-suggestion', {
                        'AddressAutocompleteList-suggestion--selected': index === selectedIndex
                      })}
                      onClick={() => onSelectSuggestion(suggestion)}
                    >
                      <div className='AddressAutocompleteList-suggestionContent'>
                        <div className='AddressAutocompleteList-suggestionTitle'>
                          <ElasticsearchHighlight
                            highlight={suggestion.highlight}
                            fallbackText={suggestion.title}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className='AddressAutocompleteList-noResults'>
              <div className='AddressAutocompleteList-noResultsIcon'>🔍</div>
              <div className='AddressAutocompleteList-noResultsText'>
                No addresses found for "{query}"
              </div>
            </div>
          )}
        </div>
      )}

      {!hasQuery && (
        <div className='AddressAutocompleteList-emptyState'>
          <div className='AddressAutocompleteList-emptyStateIcon'>🔍</div>
          <div className='AddressAutocompleteList-emptyStateText'>
            Start typing to search for addresses, neighborhoods, or areas
          </div>
        </div>
      )}
    </div>
  )
}

AddressAutocompleteList.propTypes = {
  isLoading: PropTypes.bool,
  onSelectCurrentLocation: PropTypes.func,
  onSelectSuggestion: PropTypes.func.isRequired,
  query: PropTypes.string,
  selectedIndex: PropTypes.number,
  suggestions: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    highlight: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    subtitle: PropTypes.string,
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
    url: PropTypes.string
  }))
}

export default AddressAutocompleteList
